import _get from 'lodash/get';

import {
  DEFAULT_VIDEO_BLOCK_CLASS_NAME,
  VIDEO_BLOCK_PAGE_CONTENT_QUERY,
  VIDEO_PLAYER_PLACEHOLDER_PROP_NAME,
  VIDEO_PLAYER_PROP_TO_DATA_PATH_MAP,
  VIDEO_PLAYER_SOURCE_PROP_NAME,
  VIDEO_PLAYER_URL_PROP_NAME,
  VIDEO_PLAYER_VIDEO_ID_PROP_NAME,
  getVideoPlayerDataPath,
  ARG_NAME_COMPONENT_NAME,
  ARG_NAME_COMPONENT_IDENTIFIER,
  ARG_NAME_DATA_PATH,
} from './constants';
import { getPropFunctionArgValue, getVideoPropValue } from './utils';
import { VIDEO_BLOCK_COMPONENT_NAME, VIDEO_BLOCK_ELEMENT_TYPE } from '../../../../constants/types';
import { createFishermanComponent, getComponentId } from '../utils/components';
import {
  createFinalClassName,
  extractComponentBaseProps,
  createProp,
  createComponentBaseProps,
} from '../utils/props';
import { createPageComponentQuery, getPageComponent } from '../utils/queries';

export function createVideoBlockElement(data = {}, context = {}) {
  const { id, style, className, componentName, componentIdentifier } = data;
  const { page = {}, isTemplate = false } = context;
  const { id: pageId } = page || {};

  const finalClassName = createFinalClassName(className, DEFAULT_VIDEO_BLOCK_CLASS_NAME);
  const finalPageId = isTemplate ? null : pageId;

  const defaultPropNames = Object.keys(VIDEO_PLAYER_PROP_TO_DATA_PATH_MAP);

  const props = defaultPropNames.map((propName) => {
    const dataPath = getVideoPlayerDataPath(propName);

    return createProp(propName, {
      type: 'FUNCTION',
      value: {
        name: 'getComponentConfigurationData',
        requires: '../utils/utils',
        importType: 'OBJECT',
        args: [
          {
            name: 'components',
            value: createPageComponentQuery(finalPageId, VIDEO_BLOCK_PAGE_CONTENT_QUERY),
          },
          {
            name: ARG_NAME_COMPONENT_NAME,
            value: componentName,
          },
          {
            name: ARG_NAME_COMPONENT_IDENTIFIER,
            value: componentIdentifier || null,
          },
          {
            name: ARG_NAME_DATA_PATH,
            value: dataPath,
          },
        ],
      },
    });
  });

  return createFishermanComponent(VIDEO_BLOCK_COMPONENT_NAME, id, [
    ...createComponentBaseProps(finalClassName, style),
    ...props,
  ]);
}

export function createVideoBlockState(recipeSnippet = {}, context = {}) {
  const { props } = recipeSnippet || {};
  const { state, page = {} } = context;
  const { id: pageId } = page;

  const type = VIDEO_BLOCK_ELEMENT_TYPE;
  const { className, style } = extractComponentBaseProps(recipeSnippet);
  const id = getComponentId(recipeSnippet);

  const componentName = getPropFunctionArgValue(props, {
    propName: VIDEO_PLAYER_SOURCE_PROP_NAME,
    functionArgName: ARG_NAME_COMPONENT_NAME,
    defaultValue: VIDEO_BLOCK_COMPONENT_NAME,
  });
  const componentIdentifier = getPropFunctionArgValue(props, {
    propName: VIDEO_PLAYER_SOURCE_PROP_NAME,
    functionArgName: ARG_NAME_COMPONENT_IDENTIFIER,
    defaultValue: null,
  });

  const componentOptions = {
    componentName,
    componentIdentifier,
    pageId,
  };
  const component = getPageComponent(componentOptions, state);
  const componentConfigurationId = _get(component, 'id', null);

  const url = getVideoPropValue(VIDEO_PLAYER_URL_PROP_NAME, component);
  const videoId = getVideoPropValue(VIDEO_PLAYER_VIDEO_ID_PROP_NAME, component);
  const source = getVideoPropValue(VIDEO_PLAYER_SOURCE_PROP_NAME, component);
  const placeholder = getVideoPropValue(VIDEO_PLAYER_PLACEHOLDER_PROP_NAME, component);

  return {
    id,
    className,
    style,
    type,
    componentName,
    componentIdentifier,
    componentConfigurationId,
    url,
    videoId,
    source,
    placeholder,
  };
}

export function createVideoBlockTemplateState(data = {}) {
  const { componentConfigurationId, componentIdentifier, placeholder, ...rest } = data;

  return rest;
}
